import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { StatusTag, Tag } from "../ui/Tag";
import { useT } from "../../lib/i18n";
import type { Version } from "../../lib/version";
import type { SaveState } from "../../lib/autosave";
import { vnum } from "../../lib/server-config";
import { pendingChipText, type PendingCount } from "../../lib/pending";
import { HelpVideoModal } from "../ui/HelpVideo";
import { HELP_COURSES, type HelpVideoDef } from "../../lib/helpVideos";
import { VersionPeople, versionPeopleNames } from "./VersionPeople";

const hms = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/**
 * 实时保存的状态，点开是这一版的修改记录。
 * idle 时显示服务端记的最后写入人（别人存的也算），本会话存过之后显示自己这次的。
 */
/**
 * 「还差 N 项才能发布」（设计稿 nav.jsx PendingChip）。保存不再拦「还没填完」的内容（草稿照存、发布闸拦），
 * 不放这个的话运营一路改下去，到点发布才看到一整张问题表。null = 还没检查过，不显示
 */
/** 计数只算阻断项，提示单独显示（文案见 lib/pending.ts） */
function PendingChip({ p, onOpen }: { p: PendingCount | null; onOpen: () => void }) {
  const t = useT();
  if (p == null) return null;
  const ok = p.blocking === 0;
  const clean = ok && p.warns === 0;
  return (
    <button type="button" disabled={clean} onClick={onOpen} title={t(ok ? "ver.pendingOkTip" : "ver.pendingTip")}
      className={`whitespace-nowrap rounded-full border px-2 py-px text-xs ${ok
        ? `${clean ? "cursor-default " : "hover:border-success-400 "}border-success-200 bg-success-50 text-success-800`
        : "border-warning-200 bg-warning-50 text-warning-800 hover:border-warning-400"}`}>
      {pendingChipText(t, p)}
    </button>
  );
}

function SaveStatus({ ver, save, onLog, onReload }: { ver: Version; save: SaveState; onLog: () => void; onReload: () => void }) {
  const t = useT();
  let text: string | null = null;
  let tone = "text-ink-secondary";
  if (save.state === "saving") text = t("ver.autoSaving");
  else if (save.state === "error") { text = t(save.retry ? "ver.autoRetrying" : "ver.autoFailed", { msg: save.msg }); tone = "text-danger"; }
  else if (save.state === "conflict") {
    text = ver.updatedBy && ver.updatedAtMs ? t("ver.autoConflictBy", { who: ver.updatedBy, at: hms(ver.updatedAtMs) }) : t("ver.autoConflict");
    tone = "text-danger";
  } else {
    const at = save.state === "saved" ? save.atMs : ver.updatedAtMs;
    const by = save.state === "saved" ? save.by : ver.updatedBy;
    if (at) text = t("ver.autoSaved", { at: hms(at), who: by || "—" });
  }
  if (!text) return null;
  return (
    <span className="flex items-center gap-2">
      <button type="button" role="status" title={save.state === "error" || save.state === "conflict" ? save.msg : t("ver.versionLog")} onClick={onLog}
        className={`max-w-[420px] truncate text-sm underline decoration-dotted underline-offset-4 ${tone}`}>{text}</button>
      {save.state === "conflict" && <Button size="sm" tone="primary" onClick={onReload}>{t("ver.autoReload")}</Button>}
    </span>
  );
}

/**
 * 切换版本下拉（设计稿 tabs.jsx VersionPicker）。每行两行：上面 版本号 / 状态 / 备注，下面是这一版上的人（VersionPeople）。
 * 顶上筛选框按人或备注筛，「只看我参与的」按当前登录名判；人名可点（onPerson）直达「全部版本 · 只看他」的修改记录。
 * 行尾「修改记录」打开那一版的修改记录，不切版本
 */
function VersionPicker({ versions, curVid, busy, open, setOpen, onPick, onDeleteDraft, onPerson, onLogOf, me }: {
  versions: Version[];
  curVid: string;
  busy?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  onPick: (id: string) => void;
  /** 草稿行就地删除（不用先切换过去）；确认框与真正的删除都在 App 层 */
  onDeleteDraft: (id: string) => void;
  onPerson?: (name: string) => void;
  onLogOf?: (id: string) => void;
  /** 当前登录名；没有时不出「只看我参与的」 */
  me?: string;
}) {
  const t = useT();
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  useEffect(() => {
    const h = () => setOpen(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [setOpen]);
  const kw = q.trim().toLowerCase();
  const shown = versions.filter((v) => {
    const names = versionPeopleNames(v);
    if (mine && me && !names.includes(me)) return false;
    return !kw || v.id.toLowerCase().includes(kw) || v.note.toLowerCase().includes(kw) || names.some((n) => n.toLowerCase().includes(kw));
  });
  const rowBtn = "inline-flex h-7 flex-none items-center justify-center rounded-sm px-1.5 text-xs";
  return (
    <span className="relative" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>
      <Button size="sm" disabled={busy} aria-expanded={open} onClick={() => setOpen(!open)}>{t("ver.switch")} ▾</Button>
      {open && (
        <div className="absolute left-0 top-8 z-40 w-[520px] max-w-[90vw] rounded-base border border-line-lighter bg-white p-1 shadow-light">
          <div className="flex items-center gap-2 px-1.5 pb-2 pt-1">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ver.pickFilter")} aria-label={t("ver.pickFilter")}
              className="h-7 min-w-0 flex-1 rounded-base border border-line px-2 text-sm outline-none focus:border-primary" />
            {me && (
              <label className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-ink-regular">
                <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />{t("ver.pickMine")}
              </label>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {shown.map((v) => (
              <div key={v.id} className={`group flex items-start gap-1 rounded-sm text-sm ${v.id === curVid ? "bg-primary-50" : "bg-white hover:bg-fill-light"}`}>
                <div className="min-w-0 flex-1"><button type="button" disabled={busy} aria-current={v.id === curVid ? "true" : undefined} onClick={() => { setOpen(false); onPick(v.id); }}
                  className="block w-full px-2.5 py-1.5 text-left">
                  <span className="flex items-center gap-2">
                    <span className="w-11 flex-none font-semibold">{v.id}</span>
                    <StatusTag status={v.status} />
                    <span className={`flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs ${v.note ? "text-ink-regular" : "text-ink-placeholder"}`}>{v.note || t("ver.noNote")}</span>
                  </span>
                  </button>
                <span className="mt-0.5 block px-2.5 pb-1.5">
                    <VersionPeople v={v} onPerson={onPerson ? (n) => { setOpen(false); onPerson(n); } : undefined} />
                  </span></div>
                {onLogOf && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); onLogOf(v.id); }}
                    className={`${rowBtn} mt-0.5 text-primary opacity-0 hover:bg-primary-50 focus:opacity-100 group-hover:opacity-100`}>{t("ver.versionLog")}</button>
                )}
                {/* 只有草稿给删除入口；生效中/已发布连图标都不出现，服务端也会再拦一次 */}
                {v.status === "draft" && (
                  <button type="button" disabled={busy}
                    aria-label={t("ver.rowDelete") + " " + v.id}
                    title={t("ver.rowDelete")}
                    onClick={(e) => { e.stopPropagation(); setOpen(false); onDeleteDraft(v.id); }}
                    className="mr-1 mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-sm text-ink-placeholder hover:bg-danger-50 hover:text-danger"
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path d="M2 4h12M6.5 4V2.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1V4M3.5 4l.6 9a1 1 0 0 0 1 .9h5.8a1 1 0 0 0 1-.9l.6-9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            {!shown.length && <div className="px-2.5 py-4 text-center text-sm text-ink-placeholder">{t("ver.pickNone")}</div>}
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * 版本条：版本号 / 状态 / 切换 / 保存 / 发布 / 删除草稿。
 *
 * 原来只在「总造价配置」页可操作，其余版本化页面只展示——理由是「避免四个页面
 * 各自出现一套按钮、状态互相打架」。但状态本来就是同一份（curVer/dirty 都在
 * App 上），打架是不存在的；实际代价是运营在建筑配置页改完，得先想起来
 * 「保存发布在总造价页」再跑回去（leo 2026-08-26：「为什么只有总造价管理才有
 * 发布版本」）。在哪改就在哪保存，才是运营的心智。
 */
export function VersionStrip({
  ver, versions, onlineId, dirty, editable, busy, save,
  onSwitch, onReload, onCheck, onPublish, onHistory, onDiff, onDelete, onDeleteDraft,
  onVersionLog, onPruneDrafts, onTrash, pending = null, onPending, onPerson, onVersionLogOf, me,
}: {
  /** 点人名：修改记录「全部版本 · 只看他」 */
  onPerson?: (name: string) => void;
  /** 下拉行尾「修改记录」：看某一版的修改记录，不切过去 */
  onVersionLogOf?: (id: string) => void;
  /** 当前登录名，给「只看我参与的」 */
  me?: string;
  /** 配置检查还差几项（保存后自动跑）；null = 还没检查过 */
  /** 配置检查结果：阻断项数（决定能不能发布）与提示条数。null = 还没检查过 */
  pending?: PendingCount | null;
  /** 点「还差 N 项」：打开配置检查那张表 */
  onPending?: () => void;
  ver: Version;
  versions: Version[];
  /** 当前生效版本号；租户首个草稿阶段还没有生效版本时为 null */
  onlineId: string | null;
  /** 还有没存上的改动（防抖中 / 正在存 / 存失败）。检查与发布要等它存完 */
  dirty: boolean;
  editable: boolean;
  /** 实时保存状态 */
  save: SaveState;
  /** 有版本级操作在跑（建草稿要拷贝十几张表，几秒起步）——按钮要显式说明在忙 */
  busy?: boolean;
  onSwitch: (id: string) => void;
  /** 冲突后「载入最新」 */
  onReload: () => void;
  /** 只跑一遍发布校验、不发布。发布被拒才发现问题太晚，尤其是一次四十条 */
  onCheck: () => void;
  onPublish: () => void;
  onHistory: () => void;
  onDiff: () => void;
  /** 删当前草稿；生效版本与已发布版本不给这个入口 */
  onDelete: () => void;
  /** 从「切换版本」下拉里就地删任一草稿（leo 2026-08-26：不切过去也能删） */
  onDeleteDraft: (id: string) => void;
  /** 只看这一版上发生过什么：谁建的、谁改了哪一块 */
  onVersionLog: () => void;
  /** 清掉多余草稿，只留最新那个（「一个代理一个草稿」定下之前攒的存量） */
  onPruneDrafts: () => void;
  /** 回收站：7 天内删掉的草稿，可恢复 */
  onTrash: () => void;
}) {
  const drafts = versions.filter((v) => v.status === "draft");
  const t = useT();
  const [more, setMore] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** 「更多」菜单底部的「看视频：配置版本怎么用」：就地弹第 1 课，弹窗里能接着看本门课程的下一课，不离开当前配置页 */
  const [video, setVideo] = useState<HelpVideoDef | null>(null);
  useEffect(() => {
    const close = () => setMore(false);
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setMore(false); };
    window.addEventListener("click", close);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("click", close); window.removeEventListener("keydown", key); };
  }, []);
  const action = (fn: () => void) => { setMore(false); fn(); };
  const draft = ver.status === "draft";
  const staleDraft = draft && onlineId != null && vnum(ver.id) < vnum(onlineId);
  const menuButton = "block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-fill-light disabled:text-ink-disabled";
  return (
    <section aria-label={t("ver.workspace")} className="mb-3 rounded-base border border-line-lighter bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-sm text-ink-secondary">{t(draft ? "ver.editingLabel" : "ver.viewingLabel")}</span>
            <span className="text-base font-semibold">{ver.id}</span>
            <StatusTag status={ver.status} />
            <VersionPicker versions={versions} curVid={ver.id} busy={busy} open={pickerOpen} setOpen={(open) => { setPickerOpen(open); if (open) setMore(false); }} onPick={onSwitch} onDeleteDraft={onDeleteDraft}
              onPerson={onPerson} onLogOf={onVersionLogOf} me={me} />
            {ver.from && <Tag tone="warning">{t("ver.rollbackFrom", { id: ver.from })}</Tag>}
          </div>
          {ver.user && <div className="mt-1.5 text-xs text-ink-placeholder">{t("ver.by", { who: ver.user, at: ver.time })}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {draft && <SaveStatus ver={ver} save={save} onLog={onVersionLog} onReload={onReload} />}
          {draft && editable && onPending && <PendingChip p={pending} onOpen={onPending} />}
          <span className={`rounded-base ${onlineId ? "bg-success-50" : "bg-fill-light"} px-2.5 py-1 text-sm text-ink-secondary`}>{onlineId ? t("ver.online", { id: onlineId }) : t("ver.noOnline")}</span>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" aria-expanded={more} onClick={() => { setPickerOpen(false); setMore(!more); }}>{t("ver.more")} ▾</Button>
            {more && <div className="absolute right-0 top-8 z-40 w-52 rounded-base border border-line-lighter bg-white p-1 shadow-light">
              <button type="button" className={menuButton} onClick={() => action(onVersionLog)}>{t("ver.versionLog")}</button>
              <button type="button" className={menuButton} onClick={() => action(onHistory)}>{t("ver.history")}</button>
              {onlineId && <button type="button" disabled={busy} className={menuButton} onClick={() => action(onDiff)}>{t("ver.diffOnline")}</button>}
              {drafts.length > 1 && <button type="button" disabled={busy} className={menuButton} onClick={() => action(onPruneDrafts)}>{t("ver.pruneDrafts")}</button>}
              <button type="button" disabled={busy} className={menuButton} onClick={() => action(onTrash)}>{t("ver.trash")}</button>
              {draft && ver.id !== onlineId && <div className="mt-1 border-t border-line-lighter pt-1"><button type="button" disabled={!editable || busy} className={`${menuButton} text-danger`} onClick={() => action(onDelete)}>{t("ver.delete")}</button></div>}
              <div className="mt-1 border-t border-line-lighter pt-1"><button type="button" className={`${menuButton} text-primary`} onClick={() => action(() => setVideo(HELP_COURSES.find((c) => c.id === "version")!.lessons[0]))}>{t("ver.watchCourse")}</button></div>
            </div>}
          </div>
        </div>
      </div>
      {staleDraft && onlineId && (
        <div role="alert" className="border-t border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger">
          <span className="font-semibold">{t("ver.staleDraftTitle")}</span>
          <span className="ml-2">{t("ver.staleDraftBody", { draft: ver.id, online: onlineId })}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-lighter bg-fill-light px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {busy ? <span role="status" className="text-ink-secondary">{t("ver.working")}</span>
            : <span className="text-ink-secondary">{t(draft ? "ver.draftHint" : "ver.readonlyHint")}</span>}
          {drafts.length > 1 && <Tag tone="warning">{t("ver.multiDraft", { n: drafts.length })}</Tag>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy || dirty} title={dirty ? t("ver.autoPending") : undefined} onClick={onCheck}>{t("ver.check")}</Button>
          {draft && <Button size="sm" tone="primary" disabled={!editable || busy || dirty} title={dirty ? t("ver.autoPending") : undefined} onClick={onPublish}>{t("ver.publish")}</Button>}
        </div>
      </div>
      {video && <HelpVideoModal video={video} onClose={() => setVideo(null)} onPick={setVideo} />}
    </section>
  );
}
